import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { VisuallyHidden } from "radix-ui";
import { useParams, useSearchParams } from "react-router-dom";
import { t } from "ttag";

import { deleteDocument, getDocumentQuery } from "@/app/api/legacy-api";
import { getDocumentLabel } from "@/app/components/legacy-components";
import { LogoLoader } from "@/shared/components";
import type { DocumentFolder, ModalProps } from "@/shared/models/legacy-types";
import {
  Button,
  Dialog,
  DocumentIcon,
  errorToast,
  Label,
  ShieldWarningIcon,
  UploadedFileCard,
  useToastStore,
} from "@/shared/ui";

export const RemoveDocument = ({ show, onClose }: ModalProps) => {
  const { pushToast } = useToastStore();
  const { documentId, folder } = useParams<{
    documentId: string;
    folder: DocumentFolder;
  }>();
  const [searchParams] = useSearchParams();
  const vendor = searchParams.get("vendor");
  const { data, isLoading, isSuccess } = useQuery(
    getDocumentQuery(documentId, vendor),
  );

  const queryClient = useQueryClient();
  const { mutate: deleteDocumentMutation, isPending } = useMutation({
    mutationFn: deleteDocument.mutation,
    onSuccess: () => {
      onClose();
      deleteDocument.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Document successfully deleted!`,
      });
    },
    onError: errorToast,
  });

  if (!folder || !documentId) {
    return null;
  }

  const documentLabel = getDocumentLabel(folder);

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <LogoLoader show={isLoading} withOverlay />

      {isSuccess && (
        <Dialog.Content>
          <Dialog.Header icon={ShieldWarningIcon}>
            <Dialog.Title>{t`Remove ${documentLabel}`}</Dialog.Title>

            <VisuallyHidden.Root asChild>
              <Dialog.Description>
                {t`This modal lets you remove the document.`}
              </Dialog.Description>
            </VisuallyHidden.Root>
          </Dialog.Header>

          <div className="flex flex-col gap-2">
            <Label>{t`Are you sure you want to remove the next file?`}</Label>

            <UploadedFileCard.Container id={data.id}>
              {data?.url?.includes("response-content-type=image") ? (
                <UploadedFileCard.Preview
                  src={data.url}
                  alt={data.name}
                  loading="lazy"
                />
              ) : (
                <div className="aspect-square h-full rounded-md object-cover">
                  <DocumentIcon />
                </div>
              )}

              <UploadedFileCard.Content>
                <UploadedFileCard.Title>{data.name}</UploadedFileCard.Title>
              </UploadedFileCard.Content>
            </UploadedFileCard.Container>
          </div>

          <Dialog.Footer>
            <Button onClick={onClose} size="lg" variant="secondary">
              {t`Keep file`}
            </Button>

            <Button
              onClick={() => deleteDocumentMutation({ id: documentId, vendor })}
              disabled={isPending}
              size="lg"
            >
              {t`Remove file`}
            </Button>
          </Dialog.Footer>

          <LogoLoader show={isPending} fullscreen />
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
};
